/**
 * @file assetUploadPartService.js
 * @description Orchestrates the full container asset upload flow (file transfer,
 * asset create/update, message linking) as a single unit that runs to completion
 * independent of the component that started it.
 */
import { uploadFileChunked } from './containerService';
import { createAsset, fetchAssets, updateAsset } from './clientService';
import { createMessageWithAsset } from './conversationService';
import { logEvent } from './loggingService';
import {
    formatFileSize,
    getFileCategory,
    getVideoRuntime,
    getAudioLength,
    getImageDimensions,
} from '../components/helpers/FileUtils';
import { LogEventType } from '../utils/data/enums';

const buildAssetMetaData = async file => {
    const fileCategory = getFileCategory(file.name);
    if (fileCategory === 'video') return getVideoRuntime(file);
    if (fileCategory === 'audio') return getAudioLength(file);
    if (fileCategory === 'image') return getImageDimensions(file);
    return '';
};

const findExistingAsset = async assetName => {
    const fetchedAssets = await fetchAssets(
        { pageSize: 2147483647 },
        null,
        true,
    );
    return fetchedAssets.viewModels.find(asset => asset.name === assetName);
};

/**
 * Uploads a file and links it to a container as an asset/message. Runs as one
 * atomic background task so it survives navigation away from the triggering component.
 * @returns {Promise<{id: number, name: string}>} The linked asset's id and name.
 */
export const submitContainerAssetUpload = async ({
    file,
    assetName,
    containerId,
    portalId,
    containerAssetUploadPart,
    uploadOptions,
}) => {
    const urlResponse = await uploadFileChunked(file, uploadOptions);
    const assetToUpdate = await findExistingAsset(assetName);

    const newAsset = {
        id: assetToUpdate?.id ?? 0,
        assetTypeId: 3,
        name: assetName,
        isLocked: true,
        url: urlResponse,
        fileType: file.name.split('.').pop(),
        fileSize: formatFileSize(file.size),
        description: '',
        metaData: await buildAssetMetaData(file),
        assetGroups: [],
        assetTags: ['asset upload'],
        assetIndividuals:
            containerAssetUploadPart.assetUploadPartSharingIndividuals.map(
                individual => individual.individualId.toString(),
            ),
    };

    const assetResponse = assetToUpdate
        ? await updateAsset(newAsset, null)
        : await createAsset(newAsset, null);

    await createMessageWithAsset({
        assetUploadPartId: containerAssetUploadPart.id,
        assetId: assetResponse.data.id,
    });

    logEvent(
        LogEventType.AssetUploaded,
        `Uploaded asset ${newAsset.name}`,
        portalId,
        containerId,
    );

    return { id: assetResponse.data.id, name: newAsset.name };
};
